import React, { useEffect, useState } from "react";
import { Card, Spinner, Alert, Button, Row, Col } from "react-bootstrap";
import useApi from "../../hooks/useApi";
import "./AgentDashboard.css";

const AgentDashboard = () => {
  const { bearerAuth } = useApi();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cards, setCards] = useState([]);
  const [payout, setPayout] = useState({ 
    estimated_payout_due: 0, 
    gross_payout: 0, 
    paid_payout: 0, 
    avg_per_student: 0, 
    city_breakdown: [], 
    recent_payouts: [] 
  });

  const fetchDashboard = async () => {
    try {
      setError("");
      const response = await bearerAuth("agent/dashboard");
      if (response?.data?.status) {
        const data = response.data.data || {};
        setCards(data.cards || []);
        setPayout(data.payout || { 
          estimated_payout_due: 0, 
          gross_payout: 0, 
          paid_payout: 0, 
          avg_per_student: 0, 
          city_breakdown: [], 
          recent_payouts: [] 
        });
      } else {
        setError(response?.data?.message || "Failed to load dashboard data.");
      }
    } catch (err) {
      setError(err?.message || "Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  // Mobile card for city breakdown
  const MobileCityCard = ({ row }) => (
    <div className="agent-city-breakdown-card">
      <div className="city-row">
        <span className="city-name">{row.city_name || "-"}</span>
        <span className="city-payout">₹{Number(row.estimated_payout_due || 0).toFixed(2)}</span>
      </div>
      <div className="city-students">{row.students_count || 0} students</div>
    </div>
  );

  // Mobile card for recent payouts
  const MobilePayoutCard = ({ row }) => (
    <div className="agent-recent-payout-card">
      <div className="payout-row">
        <span className="payout-date">
          {row.paid_at ? new Date(row.paid_at).toLocaleDateString() : "-"}
        </span>
        <span className="payout-amount">₹{Number(row.amount || 0).toFixed(2)}</span>
      </div>
      {row.note && <div className="payout-note">{row.note}</div>}
    </div>
  );

  if (loading) {
    return (
      <div className="agent-dashboard-loading">
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading your dashboard...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="agent-dashboard-error d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
        </div>
        <Button variant="outline-danger" size="sm" onClick={fetchDashboard}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <div className="agent-dashboard-container">
      {/* Stats Cards with Gradients */}
      <Row className="g-3 mb-4">
        {(cards || []).map((card, idx) => (
          <Col xs={12} sm={6} md={3} key={idx}>
            <Card className="agent-stats-card">
              <Card.Body>
                <div className="text-muted">{card.title}</div>
                <h3 className="mb-0">{card.value}</h3>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Main Content */}
      <Row className="g-3">
        {/* City-wise Payout Due */}
        <Col lg={7}>
          <Card className="agent-content-card">
            <Card.Body>
              <h5>City-wise Payout Due</h5>
              
              {/* Desktop Table View */}
              <div className="d-none d-md-block">
                <table className="agent-dashboard-table">
                  <thead>
                    <tr>
                      <th>City</th>
                      <th>Students</th>
                      <th>Payout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payout.city_breakdown?.length ? (
                      payout.city_breakdown.map((row) => (
                        <tr key={row.city_id || row.city_name}>
                          <td>{row.city_name || "-"}</td>
                          <td>{row.students_count || 0}</td>
                          <td className="fw-bold">₹{Number(row.estimated_payout_due || 0).toFixed(2)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="text-center text-muted py-4">
                          No payout entries yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="d-block d-md-none">
                {payout.city_breakdown?.length ? (
                  payout.city_breakdown.map((row) => (
                    <MobileCityCard key={row.city_id || row.city_name} row={row} />
                  ))
                ) : (
                  <div className="text-center text-muted py-4">
                    No payout entries yet.
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Payout Summary */}
        <Col lg={5}>
          <Card className="agent-content-card">
            <Card.Body>
              <h5>Payout Summary</h5>
              
              <div className="agent-payout-card mb-3">
                <div className="text-muted">Gross Earning</div>
                <div className="h6 mb-0">₹{Number(payout.gross_payout || 0).toFixed(2)}</div>
              </div>
              
              <div className="agent-payout-card mb-3">
                <div className="text-muted">Paid Amount</div>
                <div className="h6 mb-0">₹{Number(payout.paid_payout || 0).toFixed(2)}</div>
              </div>
              
              <div className="agent-payout-card mb-3">
                <div className="text-muted">Payout Due</div>
                <div className="h5 mb-0">₹{Number(payout.estimated_payout_due || 0).toFixed(2)}</div>
              </div>
              
              <div className="agent-payout-card">
                <div className="text-muted">Per User Earning (Avg)</div>
                <div className="h6 mb-0">₹{Number(payout.avg_per_student || 0).toFixed(2)}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Payouts */}
      <Row className="g-3 mt-1">
        <Col xs={12}>
          <Card className="agent-content-card">
            <Card.Body>
              <h5>Recent Payouts</h5>
              
              {/* Desktop Table View */}
              <div className="d-none d-md-block">
                <table className="agent-dashboard-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payout.recent_payouts?.length ? (
                      payout.recent_payouts.map((row) => (
                        <tr key={row.id}>
                          <td>{row.paid_at ? new Date(row.paid_at).toLocaleString() : "-"}</td>
                          <td className="fw-semibold">₹{Number(row.amount || 0).toFixed(2)}</td>
                          <td>{row.note || "-"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="text-center text-muted py-4">
                          No payout records yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="d-block d-md-none">
                {payout.recent_payouts?.length ? (
                  payout.recent_payouts.map((row) => (
                    <MobilePayoutCard key={row.id} row={row} />
                  ))
                ) : (
                  <div className="text-center text-muted py-4">
                    No payout records yet.
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AgentDashboard;